@file:OptIn(ExperimentalTime::class)

package com.macrofocus.eflux.ui

import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import kotlin.math.*
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

fun main() {
    val today = Clock.System.now().toLocalDateTime(TimeZone.of("Europe/Zurich")).date
    val (sunrise, sunset) = sunriseSunset(today)
    if (sunrise != null && sunset != null) {
        println("Zurich on ${today} — Sunrise: ${sunrise.toLocalDateTime(TimeZone.of("Europe/Zurich"))}, Sunset: ${sunset.toLocalDateTime(TimeZone.of("Europe/Zurich"))}")
    } else {
        println("No sunrise/sunset on $today at this latitude")
    }
}

fun sunriseSunset(
    date: LocalDate,
    // Zurich
    latitude: Double = 47.3769,
    longitude: Double = 8.5417,
    zenithAngle: Double = 90.833 // degrees: refraction + solar disc
): Pair<Instant?, Instant?> {
    // Helper: convert date to Julian Day (start at 0 UTC of date)
    fun julianDay(d: LocalDate): Double {
        val y = d.year
        val m = d.month.number
        val D = d.day.toDouble()
        val Y = if (m <= 2) y - 1 else y
        val M = if (m <= 2) m + 12 else m
        val A = floor(Y / 100.0)
        val B = 2 - A + floor(A / 4)
        return floor(365.25 * (Y + 4716)) +
                floor(30.6001 * (M + 1)) +
                D + B - 1524.5
    }

    // Solar geometric calculations
    fun sunDeclination(T: Double): Double {
        // approximate solar declination in radians
        val epsilon = 23.4397 * PI / 180
        val lambda = (280.46646 + 36000.76983 * T + 0.0003032 * T * T) % 360.0
        val M = (357.52911 + 35999.05029 * T - 0.0001537 * T * T) % 360.0
        val Mr = M * PI / 180
        val C = (1.914602 - 0.004817 * T - 0.000014 * T * T) * sin(Mr) +
                (0.019993 - 0.000101 * T) * sin(2 * Mr) +
                0.000289 * sin(3 * Mr)
        val trueLong = (lambda + C) * PI / 180
        return asin(sin(epsilon) * sin(trueLong))
    }
    fun equationOfTime(T: Double): Double {
        // approximate equation of time in minutes
        // from NOAA
        val epsilon = 23.4397 * PI / 180
        val L0 = (280.46646 + 36000.76983 * T) * PI / 180
        val e = 0.016708634 - 0.000042037 * T - 0.0000001267 * T * T
        val M = (357.52911 + 35999.05029 * T) * PI / 180
        val y = tan(epsilon / 2).pow(2)
        val Etime = y * sin(2 * L0) - 2 * e * sin(M) +
                4 * e * y * sin(M) * cos(2 * L0) -
                0.5 * y * y * sin(4 * L0) -
                1.25 * e * e * sin(2 * M)
        return 4 * (Etime * 180 / PI)  // in minutes
    }

    // Hour angle for sunrise/sunset
    fun hourAngle(lat: Double, decl: Double): Double {
        val φ = lat * PI / 180
        val cosH = (cos(zenithAngle * PI / 180) - sin(φ) * sin(decl)) /
                (cos(φ) * cos(decl))
        return if (cosH.absoluteValue <= 1.0) acos(cosH) else Double.NaN
    }

    val JD = julianDay(date)
    val T = (JD - 2451545.0) / 36525.0
    val decl = sunDeclination(T)
    val eqTime = equationOfTime(T)

    val H = hourAngle(latitude, decl) // radian; positive is sunset
    if (H.isNaN()) return null to null // polar day/night

    // Sunrise/sunset in UTC fractional hours:
    val solarNoonOffset = (720 - 4 * longitude - eqTime) // in minutes
    val halfDayMinutes = H * 180 / PI / 15 * 60
    val sunriseUTCm = solarNoonOffset - halfDayMinutes
    val sunsetUTCm  = solarNoonOffset + halfDayMinutes

    fun localDT(minutesUtc: Double): Instant {
        val minutes = minutesUtc.toLong()
        val secs = ((minutesUtc - minutes) * 60).roundToLong()
        val instant = Instant.fromEpochSeconds(
            date.toEpochDays() * 86400L + minutes * 60 + secs
        )
        return instant
//            .toLocalDateTime(TimeZone.of("Europe/Zurich"))
//            .toLocalDateTime(TimeZone.of("Europe/Zurich"))
    }

    return localDT(sunriseUTCm) to localDT(sunsetUTCm)
}
